import React, { useState, useEffect, useMemo, useRef } from "react"
import { Box, Text, Button } from "@theme-ui/components"

const ArticleSummary = ({ structuredTextData }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [activeHeading, setActiveHeading] = useState("")
  const [manuallyClicked, setManuallyClicked] = useState(false)
  const lastScrollY = useRef(0)

  // Extract h2 headings from the DAST structure (memoized to prevent re-creation)
  const headings = useMemo(() => {
    return (
      structuredTextData?.value?.document?.children?.reduce((acc, node) => {
        if (node.type === "heading" && node.level === 2) {
          // Create a slug from the heading text for the anchor link (same logic as template)
          const slug = node.children[0].value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")

          acc.push({
            text: node.children[0].value,
            slug: slug,
          })
        }
        return acc
      }, []) || []
    )
  }, [structuredTextData])

  // Handle manual scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const scrollDelta = Math.abs(currentScrollY - lastScrollY.current)

      // Se l'utente ha scrollato manualmente (più di 10px), reset del click manuale
      if (scrollDelta > 10 && manuallyClicked) {
        setManuallyClicked(false)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [manuallyClicked])

  useEffect(() => {
    if (!headings?.length) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Solo aggiorna l'active heading se non è stato cliccato manualmente
          if (entry.isIntersecting && !manuallyClicked) {
            setActiveHeading(entry.target.id)
          }
        })
      },
      {
        rootMargin: "-100px 0px -80% 0px",
        threshold: 0.1,
      }
    )

    setTimeout(() => {
      headings.forEach(({ slug }) => {
        const element = document.getElementById(slug)
        if (element) {
          observer.observe(element)
        }
      })
    }, 1000)

    return () => {
      observer.disconnect()
    }
  }, [headings, manuallyClicked])

  // Handle manual click on heading
  const handleHeadingClick = slug => {
    setActiveHeading(slug)
    setManuallyClicked(true)
  }

  if (!headings?.length) {
    return null
  }

  return (
    <Box
      sx={{
        width: ["100%", "100%", "280px"],
        flexShrink: 0,
        display: ["block"],
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: "120px",
          p: [0, 0, 4],
          maxHeight: "calc(100vh - 140px)",
          overflowY: "auto",
          mb: ["30px", "30px", "0px", "0px"],
        }}
      >
        <Box as="ul" sx={{ listStyle: "none", paddingLeft: 0, margin: 0 }}>
          {headings.map((heading, index) => {
            const isActive = activeHeading === heading.slug
            return (
              <Box
                as="li"
                key={index}
                sx={{
                  marginBottom: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "primary",
                    "& a": {
                      color: "primary",
                    },
                  },
                  "&:last-of-type": {
                    marginBottom: 0,
                  },
                  a: {
                    fontSize: "12px",
                    fontWeight: 400,
                    width: "100%",
                    py: "14px",
                    textTransform: "uppercase",
                    color: isActive ? "primary" : "dark",
                    fontWeight: isActive ? "bold" : "body",
                    textDecoration: "none",
                    borderBottom: "1px solid",
                    borderColor: "dark",
                    display: "block",
                    lineHeight: 1.4,
                    transition: "all 0.2s ease",
                    userSelect: "none",
                    "&:hover": {
                      color: "primary",
                    },
                  },
                }}
              >
                <a
                  href={`#${heading.slug}`}
                  onClick={() => handleHeadingClick(heading.slug)}
                >
                  {index + 1}. {heading.text}
                </a>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default ArticleSummary
